/**
 * Builds the URL for an edge function / CF Worker endpoint.
 *
 * All edge function calls route through CF Workers.
 * cfWorkerBaseUrl must be set in client config.
 *
 * @module edge-url
 */

import { getClientConfig } from '../client';

/** Cached base URL (normalized once on first call, reset on config change). */
let _cachedBase: string | null = null;
let _cachedConfigRef: unknown = null;

/**
 * Build the URL for a named edge function.
 *
 * @param functionName - Edge function name (e.g. 'data-proxy', 'crisis-assistance-request')
 * @returns Fully qualified URL
 * @throws If cfWorkerBaseUrl is not configured
 */
export function getEdgeFunctionUrl(functionName: string): string {
  const config = getClientConfig();
  if (!config?.cfWorkerBaseUrl) {
    throw new Error(
      'cfWorkerBaseUrl is required. Set EXPO_PUBLIC_CF_WORKER_URL in your environment.',
    );
  }

  // Cache the trailing-slash-stripped base URL; invalidate if config changes
  if (_cachedConfigRef !== config || _cachedBase === null) {
    _cachedBase = config.cfWorkerBaseUrl.replace(/\/+$/, '');
    _cachedConfigRef = config;
  }

  return `${_cachedBase}/${functionName}`;
}

/**
 * Decode a JWT payload without verifying the signature.
 * Converts base64url → base64 for atob() compatibility.
 * Returns null on any parse failure.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const b64url = token.split('.')[1];
    if (!b64url) return null;
    const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

/**
 * Build Sentry distributed tracing headers from client config.
 * Returns an empty object if tracing is not configured — safe to spread.
 */
export function getSentryTraceHeaders(): Record<string, string> {
  const config = getClientConfig();
  if (!config?.getTraceHeaders) return {};
  try {
    const th = config.getTraceHeaders();
    const headers: Record<string, string> = {};
    if (th['sentry-trace']) headers['sentry-trace'] = th['sentry-trace'];
    if (th['baggage']) headers['baggage'] = th['baggage'];
    return headers;
  } catch {
    return {};
  }
}
